import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import type { StaffOrder, TimelineEntry } from "../../api/types";
import { useAppState } from "../../state/appState";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty, Modal, StatusChip } from "../../ui/components";
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, dateTime, money } from "../../ui/format";
import { CorrectionModal } from "../CorrectionModal";
import { MoneyLine, OrderLines, OrderNotes, PhoneLink } from "../OrderTicket";

const STATUS_FILTERS = ["all", ...Object.keys(ORDER_STATUS_LABELS)] as const;

export function OrdersPage() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { run, notify } = useToasts();

  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState<StaffOrder | null>(null);
  const [refundFor, setRefundFor] = useState<StaffOrder | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [correctFor, setCorrectFor] = useState<StaffOrder | null>(null);
  const [cancelFor, setCancelFor] = useState<StaffOrder | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  const orders = snapshot?.orders ?? [];

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders.filter(order => {
      if (status !== "all" && order.status !== status) return false;
      if (!query) return true;
      return [order.number, order.customerName, order.customerPhone, order.benchId].some(value =>
        String(value ?? "").toLowerCase().includes(query)
      );
    });
  }, [orders, status, search]);

  // Актуальная версия открытого заказа: SSE обновляет снимок, и карточка
  // не должна показывать устаревшие данные.
  const openedLive = opened ? (orders.find(order => order.id === opened.id) ?? opened) : null;

  function exportCsv() {
    const header = ["Номер", "Создан", "Оплачен", "Лавочка", "Клиент", "Телефон", "Статус", "Оплата", "Сумма", "Возврат"];
    const rows = filtered.map(order => [
      order.number,
      dateTime(order.createdAt),
      dateTime(order.paidAt),
      order.benchId,
      order.customerName,
      order.customerPhone,
      ORDER_STATUS_LABELS[order.status],
      PAYMENT_STATUS_LABELS[order.paymentStatus],
      order.total,
      order.refundAmount
    ]);

    const csv = [header, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      .join("\r\n");

    // BOM — иначе Excel открывает кириллицу как крокозябры.
    const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    notify(`Выгружено заказов: ${filtered.length}`, "ok");
  }

  async function refund() {
    if (!refundFor) return;
    const parsed = refundAmount.trim() ? Number(refundAmount) : undefined;
    if (parsed !== undefined && (!Number.isFinite(parsed) || parsed <= 0)) {
      notify("Сумма возврата должна быть положительным числом", "error");
      return;
    }

    const ok = await run(
      () =>
        api.once.post(`/api/orders/${refundFor.id}/refund-test`, {
          ...(parsed === undefined ? {} : { amount: parsed }),
          reason: refundReason.trim() || undefined
        }),
      "Возврат оформлен"
    );
    if (ok) {
      setRefundFor(null);
      setRefundAmount("");
      setRefundReason("");
    }
  }

  async function cancel() {
    if (!cancelFor) return;
    const ok = await run(
      () => api.once.post(`/api/orders/${cancelFor.id}/cancel`, { reason: cancelReason.trim() }),
      `Заказ №${cancelFor.number} отменён, деньги возвращены`
    );
    if (ok) {
      setCancelFor(null);
      setCancelReason("");
    }
  }

  /** Заказ ещё можно править: он оплачен, не отменён и не доставлен. */
  function isEditable(order: StaffOrder): boolean {
    return (
      ["paid", "partial_refund"].includes(order.paymentStatus) &&
      order.status !== "cancelled" &&
      order.status !== "delivered"
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Работа с заказами</p>
          <h1>Заказы</h1>
        </div>
        {can("orders.export") ? (
          <button type="button" className="btn btn-sm" onClick={exportCsv}>
            Выгрузить CSV
          </button>
        ) : null}
      </div>

      <section className="panel">
        <div className="filters">
          <select value={status} onChange={event => setStatus(event.target.value)} aria-label="Фильтр по статусу">
            {STATUS_FILTERS.map(item => (
              <option key={item} value={item}>
                {item === "all" ? "Все статусы" : ORDER_STATUS_LABELS[item as StaffOrder["status"]]}
              </option>
            ))}
          </select>
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Номер, телефон, имя или лавочка"
            aria-label="Поиск заказа"
          />
          <span className="tiny muted">Найдено: {filtered.length}</span>
        </div>

        {filtered.length === 0 ? (
          <Empty>Ничего не найдено.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Клиент</th>
                  <th>Лавочка</th>
                  <th>Статус</th>
                  <th>Оплата</th>
                  <th>Сумма</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(order => (
                  <tr key={order.id}>
                    <td className="num">
                      №{order.number}
                      <br />
                      <small className="muted">{dateTime(order.createdAt)}</small>
                    </td>
                    <td>
                      {order.customerName}
                      <br />
                      <small className="muted num">{order.customerPhone}</small>
                    </td>
                    <td className="num">{order.benchId}</td>
                    <td>
                      <StatusChip status={order.status} />
                    </td>
                    <td className="tiny">{PAYMENT_STATUS_LABELS[order.paymentStatus]}</td>
                    <td className="num">
                      {money(order.total)}
                      {order.refundAmount ? (
                        <>
                          <br />
                          <small className="muted">возврат {money(order.refundAmount)}</small>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-sm" onClick={() => setOpened(order)}>
                          Открыть
                        </button>
                        {can("orders.correct") && isEditable(order) ? (
                          <button type="button" className="btn btn-sm" onClick={() => setCorrectFor(order)}>
                            Изменить
                          </button>
                        ) : null}
                        {can("orders.refund") && isEditable(order) ? (
                          <button type="button" className="btn btn-sm" onClick={() => setRefundFor(order)}>
                            Возврат
                          </button>
                        ) : null}
                        {can("orders.cancel") && order.status !== "cancelled" && order.status !== "delivered" ? (
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => setCancelFor(order)}>
                            Отменить
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {openedLive ? (
        <Modal
          title={`Заказ №${openedLive.number}`}
          onClose={() => setOpened(null)}
          actions={
            <button type="button" className="btn" onClick={() => setOpened(null)}>
              Закрыть
            </button>
          }
        >
          <div className="filters">
            <StatusChip status={openedLive.status} />
            <span className="chip">Лавочка {openedLive.benchId}</span>
            <span className="chip">{PAYMENT_STATUS_LABELS[openedLive.paymentStatus]}</span>
          </div>

          <p className="tiny muted">
            Создан {dateTime(openedLive.createdAt)} · оплачен {dateTime(openedLive.paidAt)} · прогноз{" "}
            {openedLive.etaMinutes} мин
          </p>

          <div>
            <strong>{openedLive.customerName}</strong>
            <PhoneLink order={openedLive} />
          </div>

          <OrderLines order={openedLive} />
          <MoneyLine order={openedLive} />
          <FiscalLine order={openedLive} />
          <OrderNotes order={openedLive} />
          {openedLive.courierName ? <p className="tiny">Доставщик: {openedLive.courierName}</p> : null}

          <OrderHistory orderId={openedLive.id} />
        </Modal>
      ) : null}

      {correctFor ? (
        <CorrectionModal
          order={orders.find(order => order.id === correctFor.id) ?? correctFor}
          onClose={() => setCorrectFor(null)}
        />
      ) : null}

      {cancelFor ? (
        <Modal
          title={`Отмена заказа №${cancelFor.number}`}
          onClose={() => setCancelFor(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setCancelFor(null)}>
                Не отменять
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void cancel()}
                disabled={!cancelReason.trim()}
              >
                Отменить и вернуть {money(cancelFor.paidAmount - cancelFor.refundAmount)}
              </button>
            </>
          }
        >
          <p>
            Приготовление и доставка будут заблокированы, клиенту вернётся вся оставшаяся сумма —{" "}
            {money(cancelFor.paidAmount - cancelFor.refundAmount)}. Действие необратимо.
          </p>
          <label className="field">
            <span>Причина отмены</span>
            <input
              autoFocus
              value={cancelReason}
              onChange={event => setCancelReason(event.target.value)}
              placeholder="Клиент не отвечает"
            />
          </label>
        </Modal>
      ) : null}

      {refundFor ? (
        <Modal
          title={`Возврат по заказу №${refundFor.number}`}
          onClose={() => setRefundFor(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setRefundFor(null)}>
                Отмена
              </button>
              <button type="button" className="btn btn-danger" onClick={() => void refund()}>
                Оформить возврат
              </button>
            </>
          }
        >
          <p>
            Возврат считается от фактически оплаченной суммы. Сейчас у клиента оплачено{" "}
            {money(refundFor.paidAmount - refundFor.refundAmount)}. Оставьте поле пустым, чтобы вернуть всё.
          </p>
          <label className="field">
            <span>Сумма возврата, ₽</span>
            <input
              autoFocus
              inputMode="numeric"
              value={refundAmount}
              onChange={event => setRefundAmount(event.target.value)}
              placeholder={String(refundFor.paidAmount - refundFor.refundAmount)}
            />
          </label>
          <label className="field">
            <span>Причина</span>
            <input
              value={refundReason}
              onChange={event => setRefundReason(event.target.value)}
              placeholder="Жалоба клиента"
            />
          </label>
          <p className="tiny muted">
            Если вернуть всю оставшуюся сумму, заказ будет отменён. Для правки состава используйте «Изменить» — там
            возврат посчитается сам.
          </p>
        </Modal>
      ) : null}
    </>
  );
}

/**
 * Неизменяемая история заказа (п. 22 ТЗ).
 *
 * Одна лента вместо трёх разрозненных списков: события, комментарий гостя,
 * заметки смены и правки состава лежат в одном порядке — том, в котором они
 * случились. Спор о том, что и когда согласовали, разрешается этим списком.
 */
/**
 * Чек по заказу: пробит ли, каким документом и дошёл ли до оператора.
 *
 * «Пробит» и «дошёл» — разные состояния, и путать их дорого. Пробитый чек
 * гость получил, а непереданный документ лежит в накопителе, у которого
 * тридцать дней до блокировки. Поэтому здесь две строки, а не одна.
 */
function FiscalLine({ order }: { order: StaffOrder }) {
  /*
   * Состояние обмена с оператором спрашивается отдельно: в снимке состояния
   * его нет, а живёт оно у кассы и рассказывает о нём агент. Один запрос
   * на открытие карточки — цена, которую можно себе позволить.
   */
  const [unsent, setUnsent] = useState<number | null>(null);

  useEffect(() => {
    if (order.fiscalStatus !== "done") return;

    let cancelled = false;
    api
      .get<{ fiscalState: { unsent: number } | null }>("/api/admin/devices")
      .then(data => {
        if (!cancelled) setUnsent(data.fiscalState?.unsent ?? null);
      })
      .catch(() => {
        // Молча: это справка, а не работа. Не узнали — так и напишем.
      });

    return () => {
      cancelled = true;
    };
  }, [order.id, order.fiscalStatus]);

  if (order.fiscalStatus === "not_required") return null;

  const fiscal = order.fiscal;

  return (
    <dl className="facts">
      <div>
        <dt>Чек</dt>
        <dd>
          {order.fiscalStatus === "done" && fiscal ? (
            <>
              ФД <b>{fiscal.document}</b> · ФП <b>{fiscal.sign}</b>
              {fiscal.simulated ? (
                <>
                  <br />
                  <small className="muted">
                    Пробит симулятором: агент кассы тогда не был запущен, настоящего документа нет.
                  </small>
                </>
              ) : null}
            </>
          ) : order.fiscalStatus === "queued" ? (
            "в очереди на кассу"
          ) : order.fiscalStatus === "failed" ? (
            <b>не пробит — приём заказов остановлен</b>
          ) : (
            "—"
          )}
        </dd>
      </div>

      {order.fiscalStatus === "done" && fiscal && !fiscal.simulated ? (
        <div>
          <dt>У оператора</dt>
          <dd>
            {unsent === null ? (
              <small className="muted">касса ещё не рассказывала о себе</small>
            ) : unsent === 0 ? (
              "все документы переданы"
            ) : (
              <>
                <b>не передано документов: {unsent}</b>
                <br />
                <small className="muted">
                  Чек пробит и выдан, но до оператора ещё не дошёл. Накопитель хранит непереданное
                  тридцать дней.
                </small>
              </>
            )}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}

function OrderHistory({ orderId }: { orderId: string }) {
  const { notify } = useToasts();

  const [items, setItems] = useState<TimelineEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);

    api
      .get<{ items: TimelineEntry[] }>(`/api/orders/${orderId}/timeline`)
      .then(data => {
        if (!cancelled) setItems(data.items);
      })
      .catch(error => {
        if (!cancelled) notify(error instanceof Error ? error.message : "Не удалось загрузить историю", "error");
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, notify]);

  if (!items) return <p className="tiny muted">Загрузка истории…</p>;
  if (items.length === 0) return null;

  return (
    <>
      <h3>История заказа</h3>
      <p className="tiny muted">Записи только добавляются: изменить или удалить событие нельзя.</p>

      <div className="history">
        {items.map(entry => (
          <div className={`history-item history-${entry.kind}`} key={entry.id}>
            <strong>{entry.title}</strong> · {dateTime(entry.at)} · {entry.actor}
            {entry.text ? (
              <>
                <br />
                {entry.text}
              </>
            ) : null}
            {entry.kind === "correction" ? <CorrectionDetails details={entry.details} /> : null}
          </div>
        ))}
      </div>
    </>
  );
}

function CorrectionDetails({ details }: { details?: Record<string, unknown> }) {
  if (!details) return null;

  const previous = (details.previous as string[] | undefined) ?? [];
  const next = (details.next as string[] | undefined) ?? [];
  const refund = Number(details.refundAmount ?? 0);

  return (
    <>
      <br />
      <span className="num">
        {money(Number(details.previousTotal ?? 0))} → {money(Number(details.newTotal ?? 0))}
      </span>
      {refund > 0 ? (
        <span className="num"> · возврат {money(refund)}</span>
      ) : (
        <span className="muted"> · без возврата</span>
      )}
      <br />
      <small className="muted">Было: {previous.join(", ") || "—"}</small>
      <br />
      <small className="muted">Стало: {next.join(", ") || "—"}</small>
      {details.note ? (
        <>
          <br />
          <small className="muted">Заметка: {String(details.note)}</small>
        </>
      ) : null}
    </>
  );
}
